import React, { useState, useRef, useEffect, useMemo } from "react";
import { Input, UploadingInput, SideSVG } from "../../../common";
import { ReactComponent as Spinner } from "../../../../assets/icons/spinner.svg";
import { ReactComponent as ArrowRight } from "../../../../assets/icons/arrowright.svg";
import { ReactComponent as ArrowLeft } from "../../../../assets/icons/arrowleft.svg";
import { ReactComponent as Close } from "../../../../assets/icons/close-circle.svg";
import { ReactComponent as TradeSVG } from "../../../../assets/images/signUp/trade.svg";
import Logo from "../../Logo";
import { useUpdateCompanyMutation } from "../../../../store/Company/companyApiSlice";
import { useSelector, useDispatch } from "react-redux";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { setClaimTradeLicenseDetails } from "../../../../store/claim/claimSlice";
import { LicensingAuthorityStateWiseDetails } from "../../../../constants/licensingAutority";
import CreatableSelectComponent from "../../../common/CreatableSelectComponent";
import { handleFormatOptions } from "../../../../utils/Helper";
import { getStatesByCountryCode } from "../../../../utils/countriesState";

export const TradeLicense = ({ onNext, onPrev }) => {
  const tradeNumberRef = useRef();
  const dispatch = useDispatch();
  const { claimTradeLicenseDetails, filledCompany } = useSelector((state) => state.claims)
  const [errors, setErrors] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [progress, setProgress] = useState(20);
  const [apiError, setApiError] = useState("");
  const [inputValue, setInputValue] = useState("");
  const { companyData } = useSelector((state) => state.company);
  const { token, userInfo } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false)
  const [apiLoading, setApiLoading] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [stateOptions, setStateOptions] = useState(handleFormatOptions(Object.keys(LicensingAuthorityStateWiseDetails)));
  const [selectedState, setSelectedState] = useState(null);
  const [jurisdictionTypeOptions, setJurisdictionTypeOptions] = useState([]);
  const [selectedJurisdictionType, setSelectedJurisdictionType] = useState(null);
  const [registrationBodiesOptions, setRegistrationBodiesOptions] = useState([]);
  const [selectedRegistrationBody, setSelectedRegistrationBody] = useState(null);  

  let countryOtherThanAEStates = [];
  useMemo(async () => {
    const { country_code } = filledCompany;
    if (country_code !== "AE") {
      const countryStates = await getStatesByCountryCode(country_code) ?? [];
      countryOtherThanAEStates = countryStates.map(state => state.name);
      setStateOptions(handleFormatOptions(countryOtherThanAEStates));
    }
  }, [filledCompany])

  const clearValue = (change) => {
    if (change === "state") {
      setSelectedJurisdictionType({value: ""});
      setSelectedRegistrationBody({value: ""});
    } else if (change === "jurisdiction") {
      setSelectedRegistrationBody(null);
    }
  }

  useMemo(() => {
    if (filledCompany.country_code === "AE" && selectedState?.value) {
      const registrationBodies = LicensingAuthorityStateWiseDetails[selectedState?.value][selectedJurisdictionType?.value];
      setRegistrationBodiesOptions(handleFormatOptions(registrationBodies));
    } else {
      setRegistrationBodiesOptions([])
    }
  }, [selectedJurisdictionType])

  useMemo(() => {
    if (filledCompany.country_code === "AE" && selectedState?.value) {
      const authorityObject = LicensingAuthorityStateWiseDetails[selectedState?.value];
      setJurisdictionTypeOptions(handleFormatOptions(Object.keys(authorityObject)));
    } else {
      setJurisdictionTypeOptions([])
    }
  }, [selectedState])

  function renderPreview() {
    if (uploadedFile.type.includes("image")) {
      return <img src={previewImage ? previewImage : claimTradeLicenseDetails?.trade_license} alt="trade" className="w-18" />;
    } else if (uploadedFile.type === "application/pdf") {
      return (
        <embed
          src={`${URL.createObjectURL(uploadedFile)}#page=1`}
          type="application/pdf"
          width="100%"
          height="500px"
        />
      );
    } else {
      return <p>Unsupported file</p>;
    }
  };

  const handleFileSelect = (file) => {
    setUploadedFile(file);
    if (file && file.type.match("image.*") || file.type.match("application/pdf")) {
      errors.tradePhotoError = "";
      const reader = new FileReader();

      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };

      reader.readAsDataURL(file);
      setErrors(errors);
    } else {
      setPreviewImage(null);
      errors.tradePhotoError = "Please upload your trade license jpg or pdf";
      setErrors(errors);
    }
    setIsFileUpload(true)
  };

  const handleInput = (event) => {
    const value = event.target.value;
    const sanitizedValue = value?.replace(/[^0-9a-zA-Z-]/g, '');
    setInputValue(sanitizedValue);
  };

  useEffect(() => {
    const inputElement = tradeNumberRef.current;

    if (inputElement) {
      inputElement.addEventListener('input', handleInput);
    }

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      if (inputElement) {
        inputElement.removeEventListener('input', handleInput);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiLoading(true)
    const errors = {};

    if (inputValue?.trim() === "") {
      errors.houseNo = "Please enter your trade license number";
      setApiLoading(false);
    }
    if(!selectedJurisdictionType){
      errors.houseNo = "Please enter Jurisdiction Type";
      setApiLoading(false);
    }
    if(!registrationBodiesOptions){
      errors.houseNo = "Please enter Registration Body";
      setApiLoading(false);
    }
    // if (uploadedFile) {
    //   setUploadedFile(null);
    //   setPreviewImage(null);
    //   errors.tradePhotoError = "Please upload your trade license jpg or pdf";
    //   setApiLoading(false);
    // }

    setErrors(errors);

    if (Object.keys(errors).length === 0) {
      try {
        const tradeLicenseUrl = isFileUpload && await uploadFileToS3(
          uploadFilePath?.tradeLicense,
          uploadedFile,
          token,
          filledCompany?.companyID || filledCompany?.wb_win,
          "trade_license_staging"
          // userInfo?.id
        );

        const jsonData = {
          trade_license: tradeLicenseUrl?.url || claimTradeLicenseDetails?.trade_license,
          license_no: inputValue,
          state: selectedState,
          JurisdictionType: selectedJurisdictionType,
          selectedRegistrationBody: selectedRegistrationBody,
        };
        dispatch(setClaimTradeLicenseDetails(jsonData))
        onNext();
        setApiLoading(false)
      } catch (error) {
        setApiError(
          error?.data
            ? Object.values(error?.data)[0]
            : "An error occurred. Please try again later."
        );
        setApiLoading(false)
      }
    }
  };
  function convertFileSize(size) {
    if (size < 1024) {
      return size + " B";
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + " KB";
    } else {
      return (size / (1024 * 1024)).toFixed(2) + " MB";
    }
  }

  return (
    <section className="relative px-4 sm:px-0 py-20 lg:py-0 overflow-hidden">
      <div className="px-0 mx-auto">
        <div className="mx-auto relative">
          <Logo />
          <div className="flex -mx-4 xl:items-center items-center max-h-screen">
            <div className="flex-flex1 w-full lg:w-1/2 xl:w-3/5 px-4 order-last lg:order-first">
              <div className="max-w-md py-20 mx-auto ">
                <h3 className="text-f24 sm:text-f30 text-primary font-bold mb-4 text-center">
                  Commercial License Information
                </h3>
                <form>
                  <div className="mb-6">
                    <Input
                      label="Commercial License No."
                      id="houseNo"
                      inputRef={tradeNumberRef}
                      inputvalue={inputValue}
                      error={errors.houseNo}
                      placeholder="Enter Commercial License No."
                    />
                    {stateOptions.length > 0 ? <CreatableSelectComponent
                      label={"States"}
                      id="state"
                      placeholder={"Select State"}
                      value={selectedState}
                      options={stateOptions}
                      setOptions={setStateOptions}
                      setSelectedValue={setSelectedState}
                      isCreatable={false}
                      classes={"mb-3"}
                      clearValue={clearValue}
                    /> : <Input
                      label={"States"}
                      id="state"
                      placeholder={"Select State"}
                      inputvalue={selectedState.value}
                      onInputChange={(e) => setSelectedState({ value: e.target.value })}
                    />}
                    {filledCompany.country_code === "AE" && (jurisdictionTypeOptions.length > 0 ? <CreatableSelectComponent
                      label={"Enter Jurisdiction"}
                      id="jurisdiction"
                      placeholder={"Select Jurisdiction"}
                      value={selectedJurisdictionType}
                      options={jurisdictionTypeOptions}
                      setOptions={setJurisdictionTypeOptions}
                      setSelectedValue={setSelectedJurisdictionType}
                      isCreatable={true}
                      classes={"mb-3"}
                      isDisabled={!selectedState}
                      clearValue={clearValue}
                    /> : <Input
                      label={"Enter Jurisdiction"}
                      id="jurisdiction"
                      placeholder={"Select Jurisdiction"}
                      inputvalue={selectedJurisdictionType?.value}
                      disable={!selectedState}
                      onInputChange={(e) => setSelectedJurisdictionType({ value: e.target.value })}
                    />)}
                    {registrationBodiesOptions?.length > 0 ? <CreatableSelectComponent
                      label={"Enter Registration Body"}
                      id="registrationBody"
                      placeholder={"Enter Registration Body"}
                      value={selectedRegistrationBody}
                      options={registrationBodiesOptions}
                      setOptions={setRegistrationBodiesOptions}
                      setSelectedValue={setSelectedRegistrationBody}
                      isCreatable={true}
                      classes={"mb-3"}
                      isDisabled={filledCompany.country_code === "AE" ? !selectedJurisdictionType : !selectedState}
                    /> :
                      <Input
                        label={"Enter Registration Body"}
                        id="registrationBody"
                        placeholder={"Enter Registration Body"}
                        inputvalue={selectedRegistrationBody?.value}
                        onInputChange={(e) => setSelectedRegistrationBody({ value: e.target.value })}
                        disable={filledCompany.country_code === "AE" ? !selectedJurisdictionType : !selectedState}
                      />}
                    <label
                      className="block mb-1 text-primary text-f14 font-semibold text-left"
                    >
                      Upload Trade License (Recommended)
                    </label>
                    {!uploadedFile && <UploadingInput
                      onFileSelect={handleFileSelect}
                      isUploading={isLoading}
                      uploadedFile={uploadedFile}
                    />}
                    {uploadedFile && (
                      <div className="bg-slate mt-2 p-3 rounded-md relative ">
                        <div className="flex gap-3">
                          <div
                            className={`flex-auto w-16 ${uploadedFile.type === "application/pdf"
                              ? "h-20"
                              : "h-14"
                              } overflow-hidden`}
                          >
                            {renderPreview()}
                          </div>
                          <div className="flex flex-col items-start justify-center text-secondarytwo">
                            <p className="text-f14">{uploadedFile.name}</p>
                            <div>
                              <span className="text-f12 text-secondary">
                                {convertFileSize(uploadedFile.size)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {isLoading && (
                          <div className="flex gap-3 items-center justify-start mt-3">
                            <div className="bg-white h-2 rounded-full overflow-hidden  relative w-full">
                              <div
                                className="bg-primary h-full transition-all"
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <span className="">{progress}%</span>
                          </div>
                        )}

                        {uploadedFile && (
                          <button
                            className="absolute right-3 top-3"
                            onClick={() => {
                              setUploadedFile(null);
                              setPreviewImage(null);
                              setApiError("");
                              setErrors({});
                            }}
                          >
                            <Close />
                          </button>
                        )}
                      </div>
                    )}
                    {/* {errors.tradePhotoError && (
                      <p className="text-red-500 text-sm text-left">
                        {errors.tradePhotoError}
                      </p>
                    )} */}
                    {apiError && (
                      <p className="text-red-500 text-sm text-left">
                        {apiError}
                      </p>
                    )}
                  </div>
                  <div className="flex justify-between gap-2">
                    <button
                      className="inline-flex items-center justify-start mr-2 gap-4 text-primaryText font-bold w-full"
                      onClick={() => onPrev()}
                    >
                      <ArrowLeft />
                      <span className="relative">Go Back</span>
                    </button>
                    <button
                      className={`relative group w-full py-3 px-5 text-center text-sm font-semibold text-white rounded-md  inline-flex items-center justify-center gap-4 ${(apiError || isLoading || apiError !== "" || !inputValue || (filledCompany.country_code === "AE" && !(selectedJurisdictionType?.value)) || !(selectedRegistrationBody?.value)) ? "cursor-not-allowed bg-disable" : "cursor-pointer bg-primary"} `}
                      onClick={handleSubmit}
                      disabled={apiLoading || isLoading || apiError !== "" || !inputValue || (filledCompany.country_code === "AE" && !(selectedJurisdictionType?.value)) || !(selectedRegistrationBody?.value)}
                    >
                      <span className="relative">
                        {isLoading || apiLoading ? <Spinner /> : "Save & Continue"}
                      </span>
                      {!apiLoading && <ArrowRight />}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <SideSVG
              SVG={TradeSVG}
              positions="top-80 transform -translate-y-1/2 left-[-25%]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
